import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { FondCommune } from '@/components/FondCommune';
import { ModulesProvider, RequireModule } from '@/lib/modules-context';
import { ThemeProvider } from '@/lib/theme-context';
import { ToastProvider } from '@/components/ui/Toast';

import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { InscriptionCollectivitePage } from '@/pages/InscriptionCollectivitePage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { ConfirmEmailChangePage } from '@/pages/ConfirmEmailChangePage';
import { DashboardPage } from '@/pages/DashboardPage';
import VueMaireConsolidee from '@/pages/dashboard/VueMaireConsolidee';
import VueMaireDetail from '@/pages/dashboard/VueMaireDetail';
import ExportConseilPage from '@/pages/dashboard/ExportConseilPage';
import { TwoFactorPage } from '@/pages/TwoFactorPage';
import { AccountPage } from '@/pages/AccountPage';
import { PublicReportPage } from '@/pages/service-technique/PublicReportPage';
import { SignalementsListPage } from '@/pages/service-technique/SignalementsListPage';
import { SignalementDetailPage } from '@/pages/service-technique/SignalementDetailPage';
import { MaTourneePage } from '@/pages/service-technique/MaTourneePage';
import { PlanningManagerPage } from '@/pages/service-technique/PlanningManagerPage';
import { ErpVisitesSecuritePage } from '@/pages/service-technique/ErpVisitesSecuritePage';
import { ContratsBatimentsPage } from '@/pages/service-technique/ContratsBatimentsPage';
import { ClesBatimentsPage } from '@/pages/service-technique/ClesBatimentsPage';
import { PresenceServiceTechniquePage } from '@/pages/service-technique/PresencePage';
import { EquipesServiceTechniquePage } from '@/pages/service-technique/EquipesPage';
import { ArretesListPage } from '@/pages/service-technique/ArretesListPage';
import { EdlTemplatesPage } from '@/pages/service-technique/EdlTemplatesPage';  // Templates état des lieux (mig 216)
import { ArreteDetailPage, ArreteNouveauPage } from '@/pages/service-technique/ArreteDetailPage';
// Module Scolaire / Cantine — lot 3
import { ScolaireDashboardPage } from '@/pages/scolaire/ScolaireDashboardPage';
import { EcolesClassesPage } from '@/pages/scolaire/EcolesClassesPage';
import { CalendrierPage } from '@/pages/scolaire/CalendrierPage';
import { FamillesPage } from '@/pages/scolaire/FamillesPage';
import { VueCuisinePage } from '@/pages/scolaire/VueCuisinePage';
import { PeriscolairePage } from '@/pages/scolaire/PeriscolairePage';
import { AlshPage } from '@/pages/scolaire/AlshPage';
import { MenusPage } from '@/pages/scolaire/MenusPage';
import { ParametresCantinePage } from '@/pages/scolaire/ParametresCantinePage';
import { TarifsPage } from '@/pages/scolaire/TarifsPage';
import { FacturationPage } from '@/pages/scolaire/FacturationPage';
import { ScolairePlanningManagerPageV2 as PlanningScolaireManagerPage } from '@/pages/scolaire/PlanningManagerPageV2';
import { MonPlanningScolairePage } from '@/pages/scolaire/MonPlanningPage';
import { ScolairePresencePage } from '@/pages/scolaire/ScolairePresencePage';
import { PointagePage } from '@/pages/scolaire/PointagePage';
import { EffectifsJourPage } from '@/pages/scolaire/EffectifsJourPage';
// Module Associations — Lot 1 (socle annuaire/fiches)
import { AssociationsPage } from '@/pages/associations/AssociationsPage';
import { AccompagnementsMairiePage } from '@/pages/associations/AccompagnementsMairiePage';
import { GrcDashboardPage } from '@/pages/grc/GrcDashboardPage';
import { GrcInboxPage, GrcMessageDetailPage } from '@/pages/grc/GrcInboxPage';
import { AlertesListPage, AlerteNouvellePage, AlerteDetailPage } from '@/pages/grc/AlertesPages';
import { AlertePresetsPage } from '@/pages/grc/AlertePresetsPage';
import { SmsConfigPage } from '@/pages/grc/SmsConfigPage';
import { PoiGestionPage } from '@/pages/grc/PoiGestionPage';
import { PoiCitoyenPage } from '@/pages/grc/PoiCitoyenPage';
import { NewsletterListPage, NewsletterEditorPage, NewsletterDetailPage } from '@/pages/grc/NewsletterPages';
import { MessagerieInternePage } from '@/pages/grc/MessagerieInternePage';
import { GrcSuiviLocationsPage } from '@/pages/grc/GrcSuiviLocationsPage';
import { GrcPointageFacturesPage } from '@/pages/grc/GrcPointageFacturesPage';
import GrcCahierPage from '@/pages/grc/GrcCahierPage';
import InfosPratiquesPage from '@/pages/grc/InfosPratiquesPage';
import GrcEtatCivilPage from '@/pages/grc/GrcEtatCivilPage'; // Item 13.1 — suivi des dossiers d'état civil
import GrcDeliberationsPage from '@/pages/grc/GrcDeliberationsPage';
import ActeNouveauPage from '@/pages/service-technique/ActeNouveauPage';
import ReferendumsPage from '@/pages/grc/ReferendumsPage';
import GrcServicesPage from '@/pages/grc/GrcServicesPage';
import ComptabilitePage from '@/pages/comptabilite/ComptabilitePage';
import PetiteEnfancePage from '@/pages/petite-enfance/PetiteEnfancePage';
import { PetiteEnfancePresencePage } from '@/pages/petite-enfance/PetiteEnfancePresencePage';
import { PetiteEnfancePlanningTravailPage } from '@/pages/petite-enfance/PetiteEnfancePlanningTravailPage';
import PetiteEnfanceEspacePage from '@/pages/petite-enfance/PetiteEnfanceEspacePage';
import { GrcDossiersScolairesPage } from '@/pages/grc/GrcDossiersScolairesPage';
import { GrcDossiersDechetteriePage } from '@/pages/grc/GrcDossiersDechetteriePage';
import { GrcDossiersPetiteEnfancePage } from '@/pages/grc/GrcDossiersPetiteEnfancePage';
import { GrcDossiersAssociationsPage } from '@/pages/grc/GrcDossiersAssociationsPage';
import { GrcSubventionsPage } from '@/pages/grc/GrcSubventionsPage';
import { GrcPlanningManagerPage } from '@/pages/grc/GrcPlanningManagerPage';  // Planning communication/GRC (présence = sous-vue)
import { ParcDashboardPage, VehiculeDetailPage, VehiculeFormPage, ScanFactureMobilePage } from '@/pages/parc/ParcPages';
import { MaterielCataloguePage, MaterielFormPage, MaterielReservationsPage } from '@/pages/materiel/MaterielPages';
import { LouerMaterielPage, MesLocationsPage } from '@/pages/materiel/MaterielReservationPages';
import { ActiveAlerteBanner } from '@/components/ActiveAlerteBanner';

// Backoffice DSI/admin — Livraison 2 (Bloc A)
import { AdminLayout } from '@/components/admin/AdminLayout';
import { UsersListPage } from '@/pages/admin/UsersListPage';
import { UserDetailPage } from '@/pages/admin/UserDetailPage';
import { UserRoleOverridePage } from '@/pages/admin/UserRoleOverridePage';  // L13.13 : promotion rôle
import { PermissionsMatrixPage } from '@/pages/admin/PermissionsMatrixPage';
import { AdminPerimetresPage } from '@/pages/admin/AdminPerimetresPage';
import { LieuxPage } from '@/pages/admin/LieuxPage';
import { DeliberationsReferentielsPage } from '@/pages/admin/DeliberationsReferentielsPage';
import { ArchivesPage } from '@/pages/admin/ArchivesPage';
import { DonneesPage } from '@/pages/admin/DonneesPage';
import { IdentitesPrestatairesPage } from '@/pages/admin/IdentitesPrestatairesPage';
import { RelecteursAdminPage } from '@/pages/admin/RelecteursAdminPage';
import { AnnuaireTiersPage } from '@/pages/admin/AnnuaireTiersPage';  // Annuaire tiers partagé (mig 213)
import { TenantModulesPage } from '@/pages/admin/TenantModulesPage';
import { ConservationRgpdPage } from '@/pages/admin/ConservationRgpdPage';
import { NotificationsCataloguePage } from '@/pages/admin/NotificationsCataloguePage';
import { EcheancesRappelsPage } from '@/pages/admin/EcheancesRappelsPage';
import { AuditLogPage } from '@/pages/admin/AuditLogPage';
import { WorkflowsPage } from '@/pages/admin/WorkflowsPage';
import { ProjetsPage } from '@/pages/admin/ProjetsPage';
import { InvitationsPage } from '@/pages/admin/InvitationsPage';
// L13.14.2 — onglets backoffice dont les routes avaient été perdues dans App.tsx
import { TenantSettingsPage } from '@/pages/admin/TenantSettingsPage';   // onglet « Mairie » (L13.7)
import { BackupsStatusPage } from '@/pages/admin/BackupsStatusPage';     // onglet « Sauvegardes » (L13.9)
import { ChorusDiagnosticPage } from '@/pages/admin/ChorusDiagnosticPage'; // onglet « Chorus Pro »
import { RhPaiePage } from '@/pages/admin/RhPaiePage';     // Brique RH : consolidation paie (page role-aware, onglets Mes demandes/À valider/…)
import { MonEspaceAgentPage } from '@/pages/agents/MonEspaceAgentPage';   // Brique « Mon espace agent » (mig 253)
import { AnnuaireAgentsPage } from '@/pages/agents/AnnuaireAgentsPage';   // Annuaire interne des agents (item 6.3, mig 286)
import { MonEspaceConfigPage } from '@/pages/admin/MonEspaceConfigPage';  // Sa configuration (mig 253)
import { DiffusionNouvellePage } from '@/pages/grc/DiffusionNouvellePage';  // Diffusions internes (mig 254)
import { DiffusionSuiviPage } from '@/pages/grc/DiffusionSuiviPage';        // Leur suivi (mig 254)
import { DelegationsPage } from '@/pages/admin/DelegationsPage';            // Délégations de brique (mig 255)
import { AcceptInvitationPage } from '@/pages/AcceptInvitationPage';
// Phase 2A core L9 — flow citoyen public anonyme
import { SignalementPublicAnonymePage } from '@/pages/SignalementPublicAnonymePage';
import { SuiviSignalementPage } from '@/pages/SuiviSignalementPage';
import { SignerPage } from '@/pages/signature/SignerPage';
import { EtatDesLocauxPage } from '@/pages/service-technique/EtatDesLocauxPage';
import { ProfilsDroitsPage } from '@/pages/admin/ProfilsDroitsPage';
import { DeportsPage } from '@/pages/admin/DeportsPage';
import { ReglagesBriquePage } from '@/pages/reglages/ReglagesBriquePage';
import { AutomatismesPage } from '@/pages/admin/AutomatismesPage';
import { MonSuiviPage } from '@/pages/administre/MonSuiviPage';
import { TitreDePage } from '@/components/TitreDePage';
import { RelirePage } from '@/pages/relecture/RelirePage';
import { MesValidationsPage } from '@/pages/relecture/MesValidationsPage';
import { PetiteEnfanceLayout } from '@/components/petite-enfance/PetiteEnfanceLayout';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { VerifierCartePage } from '@/pages/VerifierCartePage';
import { PoiVitrinePubliquePage } from '@/pages/public/PoiVitrinePubliquePage';

// Plateforme OneForAll Distribution — Livraison 3 (Bloc A.2)
import { PlatformLayout } from '@/components/platform/PlatformLayout';
import { TenantsListPage } from '@/pages/platform/TenantsListPage';
import { TenantDetailPage } from '@/pages/platform/TenantDetailPage';
import { PlatformAuditLogPage } from '@/pages/platform/PlatformAuditLogPage';
import { ImpersonationBanner } from '@/components/platform/ImpersonationBanner';
import { PlanGate } from '@/components/PlanGate';

// Pages légales (accessibles à tout le monde, connecté ou non)
import { MentionsLegalesPage } from '@/pages/legal/MentionsLegalesPage';
import { PolitiqueConfidentialitePage } from '@/pages/legal/PolitiqueConfidentialitePage';
import { CguPage } from '@/pages/legal/CguPage';
import { CgvPage } from '@/pages/legal/CgvPage';
import { AccessibilitePage } from '@/pages/legal/AccessibilitePage';

// R13 — page de suivi des signalements pour l'administré connecté
import { MesSignalementsPage } from '@/pages/MesSignalementsPage';

// L13.14 — pages espace administré différencié (livrées L13.5, câblage de
// routes perdu lors d'une refonte d'App.tsx — restauré L13.14.1).
import { MesAlertesPage } from '@/pages/administre/MesAlertesPage';
import { MesAlerteDetailPage } from '@/pages/administre/MesAlerteDetailPage';
import { MesReferendumsPage } from '@/pages/administre/MesReferendumsPage';
import { MonAssociationPage } from '@/pages/administre/MonAssociationPage';
import { MesAccompagnementsPage } from '@/pages/administre/MesAccompagnementsPage';
import { AnnuaireAssociationsPage } from '@/pages/administre/AnnuaireAssociationsPage';
import { ScolaireCantinePage } from '@/pages/administre/ScolaireCantinePage';
import { FranceConnectBientotPage } from '@/pages/FranceConnectBientotPage';
import { MesDemandesUrbanismePage } from '@/pages/administre/MesDemandesUrbanismePage';
import UrbanismeDossiersPage from '@/pages/urbanisme/UrbanismeDossiersPage';
import UrbanismeDocumentsPage from '@/pages/urbanisme/UrbanismeDocumentsPage';
import { UrbanismePresencePage } from '@/pages/urbanisme/UrbanismePresencePage';

import type { ReactNode } from 'react';
import type { UserRole, UserService } from '@roronoa-collectivites/shared-types';

/**
 * Garde « staff d'un service » : seuls admin/maire et les manager/agent DU
 * service voient l'écran de gestion. Tout autre interne (autre service) ou
 * administré est redirigé vers la vue citoyen du domaine. Conforme Règle 1 du
 * guide de création de brique (isCitoyen basé sur le service, pas le rôle seul).
 */
function RequireServiceStaff({ service, citizenPath, children }: { service: UserService; citizenPath: string; children: ReactNode }) {
  const { user, loading, baseRole } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-tech border-t-transparent" aria-label="Chargement" />
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace />;
  // Les services cumulés (servicesAdditionnels) ne sont hydratés que par /me,
  // qui suit le refresh initial. Tant que /me n'a pas répondu (baseRole encore
  // null), on ATTEND au lieu de rediriger : sinon un manager cumulant ce service
  // serait renvoyé vers la vue administré avant même que le cumul soit connu
  // (symptôme observé uniquement sur urbanisme / petite enfance, seules briques
  // protégées par ce garde de service).
  if (baseRole === null && (user.role === 'manager' || user.role === 'agent')) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-tech border-t-transparent" aria-label="Chargement" />
      </div>
    );
  }
  // Staff = admin/maire, OU manager/agent dont le service (principal OU cumulé)
  // correspond. Sans le cumul, un manager d'un autre service principal mais
  // cumulant ce service-ci était traité comme un simple administré (incohérent
  // avec la tuile du dashboard).
  const userServices = [
    ...(user.service ? [user.service] : []),
    ...(user.servicesAdditionnels ?? []),
  ];
  const isStaff =
    user.role === 'super_admin' || user.role === 'admin' ||
    ((user.role === 'manager' || user.role === 'agent') && userServices.includes(service));
  if (!isStaff) return <Navigate to={citizenPath} replace />;
  return <>{children}</>;
}

function RequireAuth({ children, roles, comptableBrique }: { children: ReactNode; roles?: UserRole[]; comptableBrique?: string }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-tech border-t-transparent"
          aria-label="Chargement"
        />
      </div>
    );
  }
  // ── Retour après connexion (29/07) ────────────────────────────────────────
  //
  // Sans ceci, scanner le QR d'une armoire en étant déconnecté envoyait sur le
  // tableau de bord : la cible était perdue, et le QR ne servait que si on
  // s'était connecté AVANT de monter au grenier. Inacceptable sur le terrain.
  //
  // `sessionStorage` et non `localStorage` : c'est une intention de navigation,
  // elle meurt avec l'onglet et ne contient aucun secret — le jeton, lui, reste
  // en mémoire (voir l'en-tête d'`api.ts`).
  if (!user) {
    try {
      const cible = window.location.pathname + window.location.search;
      if (cible && !cible.startsWith('/connexion')) {
        window.sessionStorage.setItem('vpc:apres-connexion', cible);
      }
    } catch { /* navigation privée : on perd le retour, pas l'accès */ }
    return <Navigate to="/connexion" replace />;
  }
  // Accès accordé si le rôle est autorisé, OU si l'utilisateur a l'attribut
  // comptable sur la brique requise (ex. un agent comptable GRC peut voir les factures).
  const estComptable = comptableBrique != null && (user.comptableBriques ?? []).includes(comptableBrique);
  if (roles && !roles.includes(user.role) && !estComptable) return <Navigate to="/dashboard" replace />;
  return (
    <>
      <ActiveAlerteBanner />
      {children}
    </>
  );
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Planning UNIFIÉ (20/07) — l'ancienne route /service-technique/presence.
 * Gestionnaire (manager+) → redirigé vers /planning (présence = sous-vue).
 * Agent → conserve sa page présence dédiée (il n'a pas accès à /planning).
 */
function PresenceRedirectOuAgent() {
  const { user } = useAuth();
  if (user && user.role !== 'agent') {
    return <Navigate to="/service-technique/planning" replace />;
  }
  return <PresenceServiceTechniquePage />;
}

export function App() {
  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link">
        Aller au contenu principal
      </a>
      <ThemeProvider>
        <AuthProvider>
          <ModulesProvider>
          {/* L13.15 — ToastProvider : feedback d'action global (overlay, sans layout shift) */}
          <ToastProvider>
            {/* Bannière hotline globale — affichée seulement si impersonation active */}
            <ImpersonationBanner />
            <PlanGate />
            {/* Fond d'écran de la commune (mig 294) — UNE fois pour toutes les
                pages connectées, briques comprises (décision Henrico 20/08).
                Hors connexion : rien. */}
            <FondCommune />
            {/* RGAA 8.6 (audit du 25/09) — un titre d'onglet propre à chaque écran. */}
            <TitreDePage />
            <Routes>
            {/* Landing publique */}
            <Route path="/" element={<PublicOnly><LandingPage /></PublicOnly>} />

            {/* Auth */}
            <Route path="/connexion" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/inscription" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route
              path="/inscription-collectivite"
              element={<PublicOnly><InscriptionCollectivitePage /></PublicOnly>}
            />
            <Route
              path="/mot-de-passe-oublie"
              element={<PublicOnly><ForgotPasswordPage /></PublicOnly>}
            />
            <Route
              path="/reinitialiser-mot-de-passe"
              element={<PublicOnly><ResetPasswordPage /></PublicOnly>}
            />
            <Route path="/verifier-email" element={<VerifyEmailPage />} />
            <Route path="/confirmer-email" element={<ConfirmEmailChangePage />} />

            {/* Pages légales (accessibles connecté ou non) */}
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route
              path="/politique-confidentialite"
              element={<PolitiqueConfidentialitePage />}
            />
            <Route path="/cgu" element={<CguPage />} />
            {/* CGV — relation commerciale avec la collectivité cliente (≠ CGU, qui
                régissent l'usage par les agents et les administrés). */}
            <Route path="/cgv" element={<CgvPage />} />
            {/* Alias historique : le site vitrine pointe vers /confidentialite,
                alors que la route applicative est /politique-confidentialite.
                Sans cette redirection, le lien du footer public renvoie un 404. */}
            <Route
              path="/confidentialite"
              element={<Navigate to="/politique-confidentialite" replace />}
            />
            <Route path="/accessibilite" element={<AccessibilitePage />} />

            {/* Espace utilisateur */}
            <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
            <Route path="/dashboard/maire" element={<RequireAuth roles={['super_admin', 'admin']}><VueMaireConsolidee /></RequireAuth>} />
            <Route path="/dashboard/maire/export" element={<RequireAuth roles={['super_admin', 'admin']}><ExportConseilPage /></RequireAuth>} />
            <Route path="/dashboard/maire/:module" element={<RequireAuth roles={['super_admin', 'admin']}><VueMaireDetail /></RequireAuth>} />
            <Route path="/2fa" element={<RequireAuth><TwoFactorPage /></RequireAuth>} />
            <Route path="/compte" element={<RequireAuth><AccountPage /></RequireAuth>} />

            {/* R13 — espace administré : suivi de ses signalements */}
            <Route path="/mes-signalements" element={<RequireAuth><MesSignalementsPage /></RequireAuth>} />
            {/* Chantier 7 (maquette 14) — tout le suivi de l'habitant sur une page. */}
            <Route path="/mon-suivi" element={<RequireAuth><MonSuiviPage /></RequireAuth>} />

            {/* L13.5 — espace administré différencié (4 routes).
                Restauré L13.14.1 : les tuiles des modules à venir (asso,
                scolaire/cantine, urbanisme) mènent vers une page « à venir »
                détaillant les fonctionnalités prévues. */}
            <Route
              path="/mes-alertes"
              element={
                <RequireAuth>
                  <MesAlertesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/mes-alertes/:id"
              element={
                <RequireAuth>
                  <MesAlerteDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/points-interet"
              element={
                <RequireAuth>
                  <PoiCitoyenPage />
                </RequireAuth>
              }
            />
            <Route
              path="/mes-referendums"
              element={
                <RequireAuth>
                  <MesReferendumsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/mon-association"
              element={
                <RequireAuth>
                  <MonAssociationPage />
                </RequireAuth>
              }
            />
            <Route
              path="/associations-annuaire"
              element={
                <RequireAuth>
                  <AnnuaireAssociationsPage />
                </RequireAuth>
              }
            />
            {/* Côté demandeur : association, acteur local, ou simple administré
                si la commune ouvre l'accompagnement à tous. */}
            <Route
              path="/mes-accompagnements"
              element={
                <RequireAuth>
                  <MesAccompagnementsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/scolaire-cantine"
              element={
                <RequireAuth>
                  <ScolaireCantinePage />
                </RequireAuth>
              }
            />
            <Route
              path="/connexion/franceconnect-mock"
              element={
                <RequireAuth>
                  <FranceConnectBientotPage />
                </RequireAuth>
              }
            />

            {/* Module Scolaire / Cantine — lot 3 (mairie) */}
            <Route path="/scolaire" element={<RequireModule module="scolaire_cantine"><RequireAuth roles={['super_admin', 'admin', 'manager']}><ScolaireDashboardPage /></RequireAuth></RequireModule>} />
            <Route path="/associations" element={<RequireModule module="associations"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><AssociationsPage /></RequireAuth></RequireModule>} />
            {/* 9.3 — les accompagnements proposés par la mairie. Même brique,
                mêmes rôles que l'annuaire : c'est le staff associations qui
                paramètre et qui traite. */}
            <Route path="/associations/accompagnements" element={<RequireModule module="associations"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><AccompagnementsMairiePage /></RequireAuth></RequireModule>} />
            <Route path="/scolaire/ecoles" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><EcolesClassesPage /></RequireAuth>} />
            <Route path="/scolaire/calendrier" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><CalendrierPage /></RequireAuth>} />
            <Route path="/scolaire/parametres" element={<RequireAuth roles={['super_admin', 'admin']}><ParametresCantinePage /></RequireAuth>} />
            <Route path="/scolaire/tarifs" element={<RequireAuth roles={['super_admin', 'admin']}><TarifsPage /></RequireAuth>} />
            <Route path="/scolaire/facturation" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><FacturationPage /></RequireAuth>} />
            <Route path="/scolaire/familles" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><FamillesPage /></RequireAuth>} />
            <Route path="/scolaire/cantine" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><VueCuisinePage /></RequireAuth>} />
            <Route path="/scolaire/periscolaire" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><PeriscolairePage /></RequireAuth>} />
            <Route path="/scolaire/alsh" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><AlshPage /></RequireAuth>} />
            <Route path="/scolaire/menus" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><MenusPage /></RequireAuth>} />
            <Route path="/scolaire/planning" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><PlanningScolaireManagerPage /></RequireAuth>} />
            <Route path="/scolaire/mon-planning" element={<RequireAuth roles={['agent']}><MonPlanningScolairePage /></RequireAuth>} />
            <Route path="/scolaire/presence-agents" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><ScolairePresencePage /></RequireAuth>} />
            <Route path="/rh" element={<RequireAuth roles={['agent', 'manager', 'admin', 'super_admin']}><RhPaiePage /></RequireAuth>} />
            {/* Refonte UX, chantier 1 — les réglages de chaque brique, DANS la brique. */}
            <Route path="/service-technique/reglages" element={<RequireAuth roles={['manager', 'admin', 'super_admin']}><ReglagesBriquePage brique="service_technique" /></RequireAuth>} />
            <Route path="/grc/reglages" element={<RequireAuth roles={['manager', 'admin', 'super_admin']}><ReglagesBriquePage brique="grc" /></RequireAuth>} />
            {/* Recette du 28/09 : RH et Comptabilité ont leurs réglages DANS leur page. */}
            <Route path="/rh/reglages" element={<Navigate to="/rh?onglet=parametres" replace />} />
            <Route path="/comptabilite/reglages" element={<Navigate to="/comptabilite?onglet=reglages" replace />} />
            <Route path="/scolaire/prestataires" element={<RequireAuth roles={['admin', 'super_admin']}><ScolaireLayout activeTab="parametres"><IdentitesPrestatairesPage /></ScolaireLayout></RequireAuth>} />
            <Route path="/petite-enfance/prestataires" element={<RequireAuth roles={['admin', 'super_admin']}><PetiteEnfanceLayout activeTab="parametres"><IdentitesPrestatairesPage /></PetiteEnfanceLayout></RequireAuth>} />
            {/* Vague A — « Mon espace agent » (mig 253). Brique transverse : tous
                les rôles internes, aucun filtre de service. `administre` en est
                exclu, c'est un espace interne. */}
            <Route
              path="/mon-espace"
              element={
                <RequireModule module="agents">
                  <RequireAuth roles={['agent', 'manager', 'admin', 'super_admin']}>
                    <MonEspaceAgentPage />
                  </RequireAuth>
                </RequireModule>
              }
            />
            {/* Annuaire interne des agents (item 6.3). Sous la même porte que
                « Mon espace agent » : c'est le même espace interne, ouvert de la
                même façon à tous les services, et fermé à l'administré. */}
            <Route
              path="/mon-espace/annuaire"
              element={
                <RequireModule module="agents">
                  <RequireAuth roles={['agent', 'manager', 'admin', 'super_admin']}>
                    <AnnuaireAgentsPage />
                  </RequireAuth>
                </RequireModule>
              }
            />
            <Route
              path="/admin/mon-espace"
              element={
                <RequireModule module="agents">
                  <RequireAuth roles={['manager', 'admin', 'super_admin']}>
                    <MonEspaceConfigPage />
                  </RequireAuth>
                </RequireModule>
              }
            />
            {/* Vague B — diffusions internes à retour attendu (mig 254, 5.25).
                Composer et suivre appartiennent à l'encadrement ; l'agent, lui,
                répond depuis son espace. La garde fine (portée par brique) est
                côté serveur : ici on ne fait qu'écarter les agents et les
                administrés. */}
            <Route
              path="/diffusions/nouvelle"
              element={
                <RequireAuth roles={['manager', 'admin', 'super_admin']}>
                  <DiffusionNouvellePage />
                </RequireAuth>
              }
            />
            <Route
              path="/diffusions/:id/suivi"
              element={
                <RequireAuth roles={['manager', 'admin', 'super_admin']}>
                  <DiffusionSuiviPage />
                </RequireAuth>
              }
            />
            {/* Vague C1 — délégations de brique (mig 255, item 15.6). Acte de la
                commune : le manager consulte, seul l'admin crée et termine (la
                garde fine est côté serveur, permissions delegation.*). */}
            <Route
              path="/admin/delegations"
              element={
                <RequireAuth roles={['manager', 'admin', 'super_admin']}>
                  <DelegationsPage />
                </RequireAuth>
              }
            />
            <Route path="/scolaire/pointage" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><PointagePage /></RequireAuth>} />
            <Route path="/scolaire/effectifs-jour" element={<RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}><EffectifsJourPage /></RequireAuth>} />
            <Route
              path="/mes-demandes-urbanisme"
              element={
                <RequireModule module="urbanisme">
                <RequireAuth>
                  <MesDemandesUrbanismePage />
                </RequireAuth>
                </RequireModule>
              }
            />

            {/* Urbanisme — back-office staff (migration 119) */}
            <Route path="/urbanisme" element={
              <RequireModule module="urbanisme"><RequireServiceStaff service="urbanisme" citizenPath="/mes-demandes-urbanisme"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                <UrbanismeDossiersPage tab="dossiers" />
              </RequireAuth></RequireServiceStaff></RequireModule>
            } />
            <Route path="/urbanisme/instruire" element={
              <RequireModule module="urbanisme"><RequireServiceStaff service="urbanisme" citizenPath="/mes-demandes-urbanisme"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                <UrbanismeDossiersPage tab="instruire" />
              </RequireAuth></RequireServiceStaff></RequireModule>
            } />
            <Route path="/urbanisme/decides" element={
              <RequireModule module="urbanisme"><RequireServiceStaff service="urbanisme" citizenPath="/mes-demandes-urbanisme"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                <UrbanismeDossiersPage tab="decides" />
              </RequireAuth></RequireServiceStaff></RequireModule>
            } />
            <Route path="/urbanisme/presence" element={
              <RequireModule module="urbanisme"><RequireServiceStaff service="urbanisme" citizenPath="/mes-demandes-urbanisme"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                <UrbanismePresencePage />
              </RequireAuth></RequireServiceStaff></RequireModule>
            } />
            <Route path="/urbanisme/documents" element={
              <RequireModule module="urbanisme"><RequireServiceStaff service="urbanisme" citizenPath="/mes-demandes-urbanisme"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                <UrbanismeDocumentsPage />
              </RequireAuth></RequireServiceStaff></RequireModule>
            } />

            {/* Service technique — Phase 2A.1 */}
            <Route
              path="/service-technique/signaler"
              element={<RequireAuth><PublicReportPage /></RequireAuth>}
            />
            <Route
              path="/service-technique/ma-tournee"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <MaTourneePage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/planning"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  {/*
                    24/07 — l'AGENT accède à la même page que le manager, en
                    LECTURE SEULE. La page porte déjà `isReadOnly` (role ===
                    'agent') : elle masque les actions d'écriture, le glisser-
                    déposer et les boutons de gestion. Inutile de lui construire
                    une vue à part — c'est ce qui avait produit six rendus
                    différents du même planning selon la brique.
                  */}
                  <PlanningManagerPage />
                </RequireAuth>
              }
            />
            {/* Planning UNIFIÉ (20/07) : pour les gestionnaires, la présence est
                désormais une sous-vue de la page planning → on redirige l'ancienne
                route vers /planning. L'AGENT, qui n'a pas accès à /planning
                (manager+), conserve sa page présence dédiée. */}
            <Route
              path="/service-technique/presence"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PresenceRedirectOuAgent />
                </RequireAuth>
              }
            />
            {/* Bloc 2 — gestion des équipes de travail (manager+ uniquement) */}
            <Route
              path="/service-technique/equipes"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <EquipesServiceTechniquePage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/visites-erp"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ErpVisitesSecuritePage />
                </RequireAuth>
              }
            />
            {/* Contrats & baux des bâtiments (item 7.5). Pas d'`agent` : un
                contrat porte un montant et un cocontractant, ce n'est pas une
                information de terrain. Le comptable, lui, est manager ou agent
                de SA brique — sa lecture passe par `comptable.contrat.read`,
                que le serveur accepte via `requireAnyPermission`. */}
            <Route
              path="/service-technique/contrats"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ContratsBatimentsPage />
                </RequireAuth>
              }
            />
            {/* Clés (item 11.1). L'agent y a accès en lecture : savoir où est une
                clé est une information de terrain, contrairement au montant d'un
                contrat. Le serveur cloisonne l'écriture par brique. */}
            <Route
              path="/service-technique/locaux"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <EtatDesLocauxPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/cles"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ClesBatimentsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/arretes"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <ArretesListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/etats-des-lieux/templates"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <EdlTemplatesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/arretes/nouveau"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <ArreteNouveauPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/arretes/:id"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <ArreteDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  {/* L13.13.1 — agent ré-autorisé en lecture seule (correction de la sur-restriction L13.11).
                      Les boutons d'action (créer/modifier/supprimer alertes, etc.) sont déjà cachés à l'agent
                      par les flags canEdit/canDelete/canTrigger dans les composants enfants. */}
                  <GrcDashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/planning"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcPlanningManagerPage />
                </RequireAuth>
              }
            />
            {/* Planning UNIFIÉ (20/07) : la présence GRC est désormais une sous-vue
                de /grc/planning (accessible à tous les rôles). Redirection pour ne
                pas casser les liens/bookmarks. */}
            <Route
              path="/grc/presence"
              element={<Navigate to="/grc/planning" replace />}
            />
            <Route
              path="/grc/messages"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  {/* L13.13.1 — agent ré-autorisé en lecture seule. La sidebar d'édition statut/assigné
                      dans GrcInboxPage est conditionnée par canEdit (manager+ only) donc déjà invisible
                      à l'agent. */}
                  <GrcInboxPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/messages/:id"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcMessageDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/alertes"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <AlertesListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/alertes/nouvelle"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <AlerteNouvellePage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/alertes/:id"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <AlerteDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/sms-config"
              element={
                <RequireAuth roles={['super_admin', 'admin']}>
                  <SmsConfigPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/points-interet"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PoiGestionPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/newsletters"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <NewsletterListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/newsletters/:id/edit"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <NewsletterEditorPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/newsletters/:id"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <NewsletterDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/alertes/modeles"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <AlertePresetsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/messagerie"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <MessagerieInternePage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/locations"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcSuiviLocationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/factures"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']} comptableBrique="grc">
                  <GrcPointageFacturesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/cahier"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcCahierPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/etat-civil"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcEtatCivilPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/infos-pratiques"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <InfosPratiquesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/referendums"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ReferendumsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/deliberations"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcDeliberationsPage />
                </RequireAuth>
              }
            />
            {/* 🔴 Cette route MANQUAIT : l'agenda GRC et les réservations de
                matériel y renvoyaient déjà, sur une page blanche. D4 en avait
                besoin pour les délibérations — les trois flux passent par là. */}
            <Route
              path="/actes/nouveau"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ActeNouveauPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/services"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcServicesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/comptabilite"
              element={
                <RequireModule module="comptable">
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ComptabilitePage />
                </RequireAuth>
                </RequireModule>
              }
            />
            <Route
              path="/petite-enfance"
              element={<RequireModule module="petite_enfance"><RequireServiceStaff service="petite_enfance" citizenPath="/petite-enfance/espace"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PetiteEnfancePage />
                </RequireAuth></RequireServiceStaff></RequireModule>}
            />
            <Route
              path="/petite-enfance/presence-agents"
              element={<RequireModule module="petite_enfance"><RequireServiceStaff service="petite_enfance" citizenPath="/petite-enfance/espace"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PetiteEnfancePresencePage />
                </RequireAuth></RequireServiceStaff></RequireModule>}
            />
            <Route
              path="/petite-enfance/planning-travail"
              element={<RequireModule module="petite_enfance"><RequireServiceStaff service="petite_enfance" citizenPath="/petite-enfance/espace"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PetiteEnfancePlanningTravailPage />
                </RequireAuth></RequireServiceStaff></RequireModule>}
            />
            <Route
              path="/petite-enfance/mon-planning"
              element={<RequireModule module="petite_enfance"><RequireServiceStaff service="petite_enfance" citizenPath="/petite-enfance/espace"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PetiteEnfancePlanningTravailPage />
                </RequireAuth></RequireServiceStaff></RequireModule>}
            />
            <Route
              path="/petite-enfance/:tab"
              element={<RequireModule module="petite_enfance"><RequireServiceStaff service="petite_enfance" citizenPath="/petite-enfance/espace"><RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <PetiteEnfancePage />
                </RequireAuth></RequireServiceStaff></RequireModule>}
            />
            <Route
              path="/petite-enfance/espace"
              element={
                <RequireModule module="petite_enfance">
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent', 'administre']}>
                  <PetiteEnfanceEspacePage />
                </RequireAuth>
                </RequireModule>
              }
            />
            <Route
              path="/grc/dossiers-scolaires"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcDossiersScolairesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/dossiers-dechetterie"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcDossiersDechetteriePage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/dossiers-associations"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcDossiersAssociationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/dossiers-petite-enfance"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcDossiersPetiteEnfancePage />
                </RequireAuth>
              }
            />
            <Route
              path="/grc/subventions"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <GrcSubventionsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/parc"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  {/* L13.11 — agent retiré : dashboard véhicules = gestion manager.
                      L'agent garde l'accès au scan facture via /parc/vehicules/:id/scan
                      (workflow QR code à venir L13.12+). */}
                  <ParcDashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/parc/vehicules/nouveau"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <VehiculeFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/parc/vehicules/:id/edit"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <VehiculeFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/parc/vehicules/:id/scan"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <ScanFactureMobilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/parc/vehicules/:id"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  {/* L13.11 — agent retiré : véhicules = gestion manager.
                      L'agent affecté à un véhicule le voit via sa tournée. */}
                  <VehiculeDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/materiel"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  <MaterielCataloguePage />
                </RequireAuth>
              }
            />
            <Route
              path="/materiel/reservations"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <MaterielReservationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/louer-materiel"
              element={
                <RequireAuth>
                  <LouerMaterielPage />
                </RequireAuth>
              }
            />
            <Route
              path="/mes-locations"
              element={
                <RequireAuth>
                  <MesLocationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/materiel/nouveau"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <MaterielFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/materiel/:id/edit"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager']}>
                  <MaterielFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/signalements"
              element={
                <RequireAuth roles={['super_admin', 'admin', 'manager', 'agent']}>
                  {/* L13.11 — la liste COMPLÈTE reste réservée aux managers.
                      L'agent est admis sur la route mais la page bascule sur
                      /signalements/mine : il ne voit QUE ses fiches assignées.
                      Avant ce fix, la nav lui affichait l'onglet « Fiches
                      incident » alors que la route lui était fermée → renvoi au
                      dashboard (bug préexistant, révélé le 23/07). */}
                  <SignalementsListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/service-technique/signalements/:id"
              element={<RequireAuth><SignalementDetailPage /></RequireAuth>}
            />

            {/* Backoffice DSI/admin — Livraison 2 (Bloc A) */}
            {/* AdminLayout fait sa propre garde rôle (super_admin, admin, manager). */}
            <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index element={<Navigate to="/admin/utilisateurs" replace />} />
              <Route path="utilisateurs" element={<UsersListPage />} />
              <Route path="utilisateurs/:id" element={<UserDetailPage />} />
              <Route path="utilisateurs/:id/role-override" element={<UserRoleOverridePage />} />
              <Route path="invitations" element={<InvitationsPage />} />
              <Route path="permissions" element={<PermissionsMatrixPage />} />
              {/* Bascule DGS — périmètres d'administration */}
              <Route path="perimetres" element={<AdminPerimetresPage />} />
              {/* Refonte UX, chantier 4 (mig 315) — le maire décide, la DGS administre. */}
              <Route path="profils" element={<ProfilsDroitsPage />} />
              <Route path="deports" element={<DeportsPage />} />
              {/* Référentiel de lieux communaux (S4-a, mig 203) */}
              <Route path="lieux" element={<LieuxPage />} />
              <Route path="deliberations" element={<DeliberationsReferentielsPage />} />
              <Route path="archives" element={<ArchivesPage />} />
              {/* Identités prestataires — cantine/périscolaire/PE délégués (S3, mig 210) */}
              <Route path="prestataires" element={<IdentitesPrestatairesPage />} />
              <Route path="annuaire" element={<AnnuaireTiersPage />} />
              {/* 3.7 — adresses de relecture (aussi accessibles depuis les Arrêtés) */}
              <Route path="relecteurs" element={<RelecteursAdminPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              {/* Export des données par thème (Lot C 2.6) */}
              {/* Une seule page depuis le 30/07. Les deux anciennes URL
                  redirigent : un favori ne doit pas tomber sur du vide. */}
              <Route path="donnees" element={<DonneesPage />} />
              <Route path="export-donnees" element={<Navigate to="/admin/donnees" replace />} />
              <Route path="import-donnees" element={<Navigate to="/admin/donnees" replace />} />
              {/* Vague 4a — circuits d'étapes paramétrables (mig 244) */}
              <Route path="workflows" element={<WorkflowsPage />} />
              {/* Refonte UX, chantier 5 — les automatismes en phrases. */}
              <Route path="automatismes" element={<AutomatismesPage />} />
              {/* Vague 4b — moteur de projet 10.1 (mig 245) */}
              <Route path="projets" element={<ProjetsPage />} />
              {/* L13.14.2 — routes restaurées (onglets AdminLayout présents mais
                  câblage perdu lors d'une refonte d'App.tsx). */}
              <Route path="parametres-mairie" element={<TenantSettingsPage />} />
              <Route path="modules" element={<TenantModulesPage />} />
              <Route path="sauvegardes" element={<BackupsStatusPage />} />
              <Route path="chorus" element={<ChorusDiagnosticPage />} />
              <Route path="rh-paie" element={<RhPaiePage />} />
              <Route path="conservation" element={<ConservationRgpdPage />} />
              {/* N2 — catalogue des notifications (lecture seule) */}
              <Route path="notifications" element={<NotificationsCataloguePage />} />
              {/* Échéances & rappels — socle réglable (Lot B, mig 211) */}
              <Route path="echeances" element={<EcheancesRappelsPage />} />
            </Route>

            {/* Page publique d'acceptation d'invitation magic-link — Livraison 6 */}
            <Route path="/invitation/:token" element={<AcceptInvitationPage />} />

            {/* Phase 2A core L9 — flow citoyen public anonyme (sans auth) */}
            <Route path="/signaler/:slug" element={<SignalementPublicAnonymePage />} />
            <Route path="/commune/:slug/points-interet" element={<PoiVitrinePubliquePage />} />
            <Route path="/suivi" element={<SuiviSignalementPage />} />
            <Route path="/suivi/:code" element={<SuiviSignalementPage />} />
            {/* M1 — signature simple : page publique par jeton (sans compte). */}
            <Route path="/signer/:token" element={<SignerPage />} />
            {/* Relecture d'un arrêté par un relecteur externe, sans compte (mig 323-324). */}
            <Route path="/relire/:token" element={<RelirePage />} />
            {/* Les validations d'arrêtés confiées à l'utilisateur (tout le personnel). */}
            <Route path="/mes-relectures" element={<RequireAuth roles={['agent', 'manager', 'admin', 'super_admin']}><MesValidationsPage /></RequireAuth>} />
            <Route path="/verifier-carte/:token" element={<VerifierCartePage />} />

            {/* Plateforme OneForAll Distribution — Livraison 3 (Bloc A.2) */}
            {/* PlatformLayout fait sa propre garde sur le flag isPlatformAdmin. */}
            <Route path="/platform" element={<RequireAuth><PlatformLayout /></RequireAuth>}>
              <Route index element={<Navigate to="/platform/collectivites" replace />} />
              <Route path="collectivites" element={<TenantsListPage />} />
              <Route path="collectivites/:id" element={<TenantDetailPage />} />
              <Route path="audit-log" element={<PlatformAuditLogPage />} />
            </Route>

            {/* Catch-all → landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ToastProvider>
          </ModulesProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
